/* Poptávka na NÁKUP nemovitosti — jediné místo, kde na ni frontend sahá.
 *
 * PROČ ADAPTÉR: kontrakt s backendem je na jednom místě. Když se routa
 * přejmenuje, mění se tenhle soubor, ne tři obrazovky.
 *
 * ⚠️ SELHÁNÍ SE NEPŘEVÁDÍ NA PRÁZDNO. „Kupující nemá žádnou nabídku“ a
 * „nepodařilo se to načíst“ jsou dvě různé věty. Kdyby chyba tiše vracela
 * prázdný seznam, makléř by nabídl podruhé byt, který už jednou nabídl.
 *
 * ⚠️ FÁZE SE SEM NEUKLÁDÁ. `stav_index` drží OBCHOD na serveru; frontend ho
 * nikdy nepočítá sám — jinak by po přetažení karty na /obchody ukazoval
 * starou fázi.
 */
import api from "@/lib/api";

const ZAKLAD = "/api/reality/poptavky";

/** Vždy pole objektů — ne-pole ani null nesmí projít dál jako data. */
const pole = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : []);

/** Moje poptávky na nákup. Vrací { poptavky: [], faze: [] }. */
export async function nactiPoptavky() {
  const { data } = await api.get(ZAKLAD);
  const poptavky = Array.isArray(data?.poptavky) ? data.poptavky : null;
  if (!poptavky) throw new Error("neocekavana-odpoved");
  return {
    poptavky: poptavky.filter(Boolean),
    faze: Array.isArray(data?.faze) ? data.faze : [],
  };
}

/** Karta jedné poptávky. */
export async function nactiPoptavku(id, jazyk = "cs") {
  const { data } = await api.get(`${ZAKLAD}/${id}`, { params: { jazyk } });
  if (!data || !data.id) throw new Error("neocekavana-odpoved");
  return normalizuj(data);
}

/** Založení. Povinný je jen klient — zbytek se doplňuje za pochodu. */
export async function zalozPoptavku(payload) {
  const { data } = await api.post(ZAKLAD, payload);
  if (!data || !data.id) throw new Error("neocekavana-odpoved");
  return data;
}

/**
 * Uložení toho, co makléř zjistil. POSÍLÁ SE JEN ZMĚNĚNÉ POLE — kdyby šel
 * pokaždé celý formulář, přepsala by se lokalita prázdnem vždycky, když se
 * karta otevřela dřív, než se dotáhla data.
 */
export async function upravPoptavku(id, zmeny, jazyk = "cs") {
  const { data } = await api.patch(`${ZAKLAD}/${id}`, zmeny, { params: { jazyk } });
  if (!data || !data.id) throw new Error("neocekavana-odpoved");
  return normalizuj(data);
}

/** Nabídnout kupujícímu nemovitost z vlastní zakázky. Vrací nový seznam nabídek. */
export async function nabidniNemovitost(id, zakazkaId, jazyk = "cs") {
  const { data } = await api.post(`${ZAKLAD}/${id}/nabidky`, { zakazka_id: zakazkaId },
                                  { params: { jazyk } });
  return pole(data?.nabidky);
}

/** Stáhnout nabídku. Vrací nový seznam nabídek. */
export async function stahniNabidku(id, zajemceId, jazyk = "cs") {
  const { data } = await api.delete(`${ZAKLAD}/${id}/nabidky/${zajemceId}`,
                                    { params: { jazyk } });
  return pole(data?.nabidky);
}

/** Číselníky (druhy, dispozice, financování). Jeden zdroj pro select i validaci. */
export async function nactiCiselniky(jazyk = "cs") {
  const { data } = await api.get(`${ZAKLAD}/ciselniky`, { params: { jazyk } });
  return {
    typy: Array.isArray(data?.typy) ? data.typy : [],
    dispozice: Array.isArray(data?.dispozice) ? data.dispozice : [],
    financovani: Array.isArray(data?.financovani) ? data.financovani : [],
  };
}

/** Tvar, na který se obrazovka smí spolehnout. Chybějící seznam = prázdný. */
function normalizuj(data) {
  return {
    ...data,
    poptavka: (data.poptavka && typeof data.poptavka === "object") ? data.poptavka : {},
    chybi: Array.isArray(data.chybi) ? data.chybi.filter(Boolean) : [],
    chybi_nazvy: Array.isArray(data.chybi_nazvy) ? data.chybi_nazvy.filter(Boolean) : [],
    nabidky: pole(data.nabidky),
    doporucene: pole(data.doporucene),
  };
}

/** Posun kupujícího po jeho cestě. Fázi drží OBCHOD na serveru — sem se
 *  posílá jen cílový index a zpátky přijde celá karta. */
export async function posunFaziPoptavky(id, stageIndex, jazyk = "cs") {
  const { data } = await api.post(`${ZAKLAD}/${id}/faze`, { stage_index: stageIndex },
                                  { params: { jazyk } });
  if (!data || !data.id) throw new Error("neocekavana-odpoved");
  return normalizuj(data);
}
